import React from "react";
import { Modal } from "react-bootstrap";
import { MapPin, FileText, Download, ExternalLink, X } from "lucide-react";
import NoProfile from "assets/images/NoProfile.png";
import './ResumeModal.css';

export interface ResumeModalProps {
  show: boolean;
  onHide: () => void;
  candidateName: string;
  designation?: string;
  location?: string;
  resumeUrl?: string | null;
  resumeLabel?: string;
  profilePic?: string;
}

function getInitials(name: string): string {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

const ResumeModal: React.FC<ResumeModalProps> = ({
  show,
  onHide,
  candidateName,
  designation,
  location,
  resumeUrl,
  profilePic,
}) => {
  const initials = getInitials(candidateName);
  const cleanUrl = resumeUrl?.split("?")[0]?.toLowerCase() || "";
  const isPdf = cleanUrl.endsWith(".pdf") || resumeUrl?.toLowerCase().includes(".pdf");
  const safeResumeUrl = resumeUrl ? encodeURI(resumeUrl) : "";

  const previewSrc =
    isPdf && safeResumeUrl
      ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(safeResumeUrl)}`
      : null;

  const openResume = () => {
    if (resumeUrl) window.open(resumeUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="xl"
      dialogClassName="resume-modal-dialog"
      contentClassName="rm-content"
      backdropClassName="rm-backdrop"
    >
      {/* Header */}
      <div className="rm-header">
        <div className="rm-header-left">
          {profilePic ? (
            <img
              src={profilePic}
              alt={candidateName}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = NoProfile;
              }}
              className="rm-avatar"
            />
          ) : (
            <div className="rm-avatar rm-avatar-fallback">{initials}</div>
          )}

          <div style={{ minWidth: 0 }}>
            <div className="rm-name">{candidateName}</div>
            <div className="rm-meta">
              {designation && <span className="rm-designation">{designation}</span>}
              {location && (
                <span className="rm-location">
                  <MapPin size={12} />
                  {location}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="rm-header-actions">
          {resumeUrl && (
            <button
              type="button"
              className="rm-btn rm-btn-sm rm-btn-ghost"
              onClick={openResume}
              title="Open in new tab"
            >
              <ExternalLink size={14} />
              <span className="rm-btn-label-hide-sm">Open in Tab</span>
            </button>
          )}

          <button
            type="button"
            onClick={onHide}
            className="rm-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body / Resume preview frame */}
      <div className="rm-body">
        <div className="rm-preview">
          {resumeUrl ? (
            isPdf ? (
              <iframe
                src={previewSrc || ""}
                title={`${candidateName} Resume`}
                className="rm-iframe"
              />
            ) : (
              <div className="rm-empty">
                <div className="rm-empty-icon">
                  <FileText size={28} />
                </div>
                <div className="rm-empty-title">In-Browser Preview Not Available</div>
                <div className="rm-empty-desc">
                  This document format cannot be rendered directly in browser. Click below to
                  download or view.
                </div>
                <button
                  type="button"
                  className="rm-btn rm-btn-primary mt-2"
                  onClick={openResume}
                >
                  <Download size={15} />
                  Download Resume Document
                </button>
              </div>
            )
          ) : (
            <div className="rm-empty">
              <div className="rm-empty-icon is-muted">
                <FileText size={28} />
              </div>
              <div className="rm-empty-title">No Resume Uploaded Yet</div>
              <div className="rm-empty-desc">
                This candidate has not uploaded a resume document to their profile.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="rm-footer">
        <button type="button" onClick={onHide} className="rm-btn rm-btn-ghost">
          Close
        </button>

        {resumeUrl && (
          <button type="button" onClick={openResume} className="rm-btn rm-btn-primary">
            <Download size={15} />
            Download Resume
          </button>
        )}
      </div>
    </Modal>
  );
};

export default ResumeModal;